"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import type {
  CVideoGalleryOuterContent,
  CVideoGalleryOuterItem,
  CVideoGalleryOuterVideo,
} from "@/lib/api/cms-catalog";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import { useT } from "@/lib/i18n/context";
import {
  cVideoGalleryOuterTranslations,
  type CVideoGalleryOuterTranslations,
} from "./CVideoGalleryOuter.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CVideoGalleryOuter.module.css";

/**
 * CVideoGalleryOuter - visitor view komponentu `c_video_gallery_outer`
 * (galeria filmów z YouTube / Vimeo). Filtr fraza + paginacja client-side.
 *
 * `text` to HTML z admin panelu (trusted) - OK dla `dangerouslySetInnerHTML`.
 */
export function CVideoGalleryOuter({ content }: CVideoGalleryOuterProps) {
  const t = useT(cVideoGalleryOuterTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  if (content.view === "article" && content.article) {
    return (
      <ArticleView
        article={content.article}
        listHref={content.list_href ?? null}
        t={t}
      />
    );
  }

  return <ListView content={content} t={t} />;
}

export type CVideoGalleryOuterProps = {
  content: CVideoGalleryOuterContent;
};

/* ─── List view ───────────────────────────────────────────────────────────── */

function ListView({
  content,
  t,
}: {
  content: CVideoGalleryOuterContent;
  t: CVideoGalleryOuterTranslations;
}) {
  const items = useMemo(
    () => (Array.isArray(content.items) ? content.items : []),
    [content.items],
  );
  const perPage = content.config?.per_page && content.config.per_page > 0 ? content.config.per_page : 10;

  const loc = useCatalogLocation();

  const [query, setQuery] = useState("");

  /* Filtr client-side po frazie (name + text). Galeria ładuje wszystkie filmy
     z SSR, więc filtr działa na pełnym zbiorze. */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const haystack = `${it.name} ${stripHtml(it.text)}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query]);

  const filterActive = query.trim() !== "";

  /* Strona startowa: bez filtra honorujemy `pageN` z URL (deep-link / SEO -
     `/galeria-video/page2` pokazuje od razu strony 1..2). Z aktywnym filtrem
     zawsze od strony 1 (URL strony nie odzwierciedla przefiltrowanego zbioru). */
  const initialPage = filterActive ? 1 : Math.max(1, loc.page);

  /* Pierwsza partia (1..initialPage). Stabilna referencyjnie (useMemo) - inaczej
     reset w CatalogLoadMore odpalałby się co render. */
  const initialItems = useMemo(
    () => filtered.slice(0, initialPage * perPage),
    [filtered, perPage, initialPage],
  );

  return (
    <div className={styles.root}>
      <div className={styles.filterBar}>
        <input
          type="text"
          className={styles.search}
          placeholder={t.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t.searchPlaceholder}
        />
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        <CatalogLoadMore<CVideoGalleryOuterItem>
          /* remount (reset paginacji do 1) gdy zmienia się filtr */
          key={query}
          initialItems={initialItems}
          initialPage={initialPage}
          totalPages={Math.max(1, Math.ceil(filtered.length / perPage))}
          totalCount={filtered.length}
          /* tryb kliencki: slice z przefiltrowanego zbioru w pamięci */
          loadPage={(p) => filtered.slice((p - 1) * perPage, p * perPage)}
          /* URL aktualizujemy tylko gdy NIE ma filtra (strona /pageN
             odzwierciedlałaby pełny zbiór, nie przefiltrowany) */
          updateUrl={!filterActive}
          getKey={(item) => item.id}
        >
          {(pageItems) => (
            <ul className={styles.grid}>
              {pageItems.map((item) => (
                <li key={item.id} className={styles.card}>
                  <VideoEmbed video={item.video} title={item.name} t={t} href={item.href} />
                  {item.name && (
                    <h2 className={styles.title}>
                      <CardLink href={item.href}>{item.name}</CardLink>
                    </h2>
                  )}
                  {item.text && (
                    <div className={styles.text} dangerouslySetInnerHTML={{ __html: item.text }} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </CatalogLoadMore>
      )}
    </div>
  );
}

/* ─── Article view ───────────────────────────────────────────────────────── */

function ArticleView({
  article,
  listHref,
  t,
}: {
  article: CVideoGalleryOuterItem;
  listHref: string | null;
  t: CVideoGalleryOuterTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;

  return (
    <div className={styles.root}>
      {(backHref !== null || listHref) && (
        <div className={styles.articleBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.articleBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.articleBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <article className={styles.article}>
        {article.name && <h2 className={styles.articleTitle}>{article.name}</h2>}
        <VideoEmbed video={article.video} title={article.name} t={t} href={article.href} />
        {article.text && (
          <div
            className={styles.articleText}
            dangerouslySetInnerHTML={{ __html: article.text }}
          />
        )}
      </article>
    </div>
  );
}

/* ─── Video embed ────────────────────────────────────────────────────────── */

function VideoEmbed({
  video,
  title,
  t,
  href,
}: {
  video: CVideoGalleryOuterVideo | null;
  title: string;
  t: CVideoGalleryOuterTranslations;
  href: string;
}) {
  const src = embedSrc(video);

  if (src === null) {
    /* Nieznany provider / brak URL - fallback do linku (gdy mamy oryginalny URL). */
    const fallbackUrl = video?.url ?? null;
    return (
      <div className={styles.videoFallback}>
        {fallbackUrl ? (
          <a href={fallbackUrl} target="_blank" rel="noopener noreferrer">
            {t.unsupportedVideo}
          </a>
        ) : (
          <CardLink href={href}>{title}</CardLink>
        )}
      </div>
    );
  }

  return (
    <div className={styles.videoWrap}>
      <iframe
        className={styles.videoFrame}
        src={src}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

/** Buduje URL embed dla iframe (YouTube / Vimeo). null gdy nieobsługiwany. */
function embedSrc(video: CVideoGalleryOuterVideo | null): string | null {
  if (!video || !video.video_id) return null;
  if (video.provider === "youtube") {
    return `https://www.youtube.com/embed/${encodeURIComponent(video.video_id)}?rel=0&modestbranding=1`;
  }
  if (video.provider === "vimeo") {
    return `https://player.vimeo.com/video/${encodeURIComponent(video.video_id)}`;
  }
  return null;
}

/** Wewnętrzny <Link> dla URL-i z backendu; natywny <a> dla zewnętrznych. */
function CardLink({ href, children }: { href: string; children: React.ReactNode }) {
  const internal = extractInternalHref(href);
  if (internal !== null) {
    return (
      <Link href={internal} className={styles.cardLink}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={styles.cardLink}>
      {children}
    </a>
  );
}

/** Usuwa tagi HTML do filtrowania tekstu (client-side search). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ");
}
